import React from 'react';
import { X, Edit, Calendar, Clock } from 'lucide-react';
import type { Itinerary } from '../../../../../types/itineraryMaintenance';
import './ItineraryDetailsModal.css';

interface ItineraryDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  itinerary: Itinerary | null;
  onEdit?: (itinerary: Itinerary) => void;
}

const ItineraryDetailsModal: React.FC<ItineraryDetailsModalProps> = ({
  isOpen,
  onClose,
  itinerary,
  onEdit
}) => {
  if (!isOpen || !itinerary) {
    return null;
  }

  const handleEdit = () => {
    if (onEdit) {
      onEdit(itinerary);
    }
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  return (
    <div className="itinerary-modal-overlay" onClick={onClose}>
      <div className="itinerary-modal-container" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="itinerary-modal-header">
          <div className="itinerary-modal-header-left">
            <div className="itinerary-modal-icon">
              <div className={`status-dot ${itinerary.isActive ? 'active' : 'inactive'}`}></div>
            </div>
            <div className="itinerary-modal-title-section">
              <h2 className="itinerary-modal-title">Itinerary Details</h2>
              <div className="itinerary-modal-subtitle">
                {itinerary.itineraryCode} - {itinerary.itineraryName}
              </div>
            </div>
          </div>
          <div className="itinerary-modal-header-actions">
            <button
              className="itinerary-modal-edit-btn"
              onClick={handleEdit}
              title="Edit Itinerary"
            >
              <Edit size={16} />
              Edit
            </button>
            <button
              className="itinerary-modal-close-btn"
              onClick={onClose}
              title="Close Modal"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Modal Content */}
        <div className="itinerary-modal-content">
          <div className="itinerary-details-grid">
            {/* Basic Information */}
            <div className="itinerary-detail-section">
              <h3 className="itinerary-detail-section-title">Basic Information</h3>
              <div className="itinerary-detail-grid">
                <div className="itinerary-detail-item">
                  <label className="itinerary-detail-label">Itinerary Code</label>
                  <div className="itinerary-detail-value itinerary-code-value">{itinerary.itineraryCode}</div>
                </div>
                <div className="itinerary-detail-item">
                  <label className="itinerary-detail-label">Itinerary Name</label>
                  <div className="itinerary-detail-value">{itinerary.itineraryName}</div>
                </div>
                <div className="itinerary-detail-item">
                  <label className="itinerary-detail-label">Status</label>
                  <div className="itinerary-detail-value">
                    <div className={`status-indicator ${itinerary.isActive ? 'active' : 'inactive'}`}>
                      <div className={`status-dot ${itinerary.isActive ? 'active' : 'inactive'}`}></div>
                      {itinerary.isActive ? 'Active' : 'Inactive'}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* System Information */}
            <div className="itinerary-detail-section">
              <h3 className="itinerary-detail-section-title">System Information</h3>
              <div className="itinerary-detail-grid">
                <div className="itinerary-detail-item">
                  <label className="itinerary-detail-label">
                    <Calendar size={14} />
                    Created Date
                  </label>
                  <div className="itinerary-detail-value">{formatDate(itinerary.createdAt)}</div>
                </div>
                {itinerary.lastModified && (
                  <div className="itinerary-detail-item">
                    <label className="itinerary-detail-label">
                      <Clock size={14} />
                      Last Modified
                    </label>
                    <div className="itinerary-detail-value">{formatDate(itinerary.lastModified)}</div>
                  </div>
                )}
                <div className="itinerary-detail-item">
                  <label className="itinerary-detail-label">Record ID</label>
                  <div className="itinerary-detail-value system-id">{itinerary.id}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="itinerary-modal-footer">
          <div className="itinerary-modal-footer-info">
            <span className="itinerary-modal-footer-text">
              Itinerary record for {itinerary.itineraryCode} - {itinerary.itineraryName}
            </span>
          </div>
          <div className="itinerary-modal-footer-actions">
            <button
              className="itinerary-modal-btn secondary"
              onClick={onClose}
            >
              Close
            </button>
            <button
              className="itinerary-modal-btn primary"
              onClick={handleEdit}
            >
              <Edit size={16} />
              Edit Itinerary
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ItineraryDetailsModal;